/* eslint-disable react/display-name */
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'

import {
  Button,
  BUTTON_THEME,
  Icon,
  IconButton,
  ICON_TYPES,
  Card,
  Divider,
} from '@st-operations/uikit/src/v2/index.js'
import Header from '_components/header/index.jsx'
import MoreInfoDropdown from '../../../../components/more-info-dropdown/index.jsx'
import { useDropdown } from '_hooks'

import './styles.scss'

export const STATUSES = {
  DRAFT: 'draft',
  APPROVED: 'approved',
  ARCHIVED: 'archived',
  PROCESSED: 'processed',
  SUBMITTED: 'submitted',
  EXPIRED: 'expired',
}

const ACTIONS = {
  SAVE_AS_DRAFT: 'SaveAsDraft',
  APPROVE: 'Approve',
  SAVE: 'Save',
  ARCHIVE: 'Archive',
  DELETE: 'Delete',
  EXIT: 'Exit',
}

const STATE_TO_ACTIONS = {
  DRAFT: {
    header: [ACTIONS.SAVE_AS_DRAFT, ACTIONS.APPROVE],
    dropdown: [ACTIONS.DELETE],
  },
  APPROVED: {
    header: [],
    dropdown: [ACTIONS.ARCHIVE, ACTIONS.DELETE],
  },
  APPROVED_WITH_CHANGES: {
    header: [ACTIONS.SAVE],
    dropdown: [ACTIONS.ARCHIVE, ACTIONS.DELETE],
  },
  ARCHIVED: {
    header: [ACTIONS.APPROVE],
    dropdown: [ACTIONS.DELETE],
  },
  PROCESSED: {
    header: [],
    dropdown: [ACTIONS.ARCHIVE, ACTIONS.DELETE],
  },
  PROCESSED_WITH_CHANGES: {
    header: [ACTIONS.SAVE],
    dropdown: [ACTIONS.ARCHIVE, ACTIONS.DELETE],
  },
  DELETED: {
    header: [],
    dropdown: [],
  },
}

const STATE_TO_ACTIONS_TAXONOMY = {
  DRAFT: {
    header: [ACTIONS.SAVE_AS_DRAFT, ACTIONS.APPROVE],
    dropdown: [],
  },
  APPROVED: {
    header: [],
    dropdown: [ACTIONS.ARCHIVE],
  },
  APPROVED_WITH_CHANGES: {
    header: [ACTIONS.SAVE],
    dropdown: [ACTIONS.ARCHIVE],
  },
  ARCHIVED: {
    header: [ACTIONS.APPROVE],
    dropdown: [],
  },
  PROCESSED: {
    header: [],
    dropdown: [ACTIONS.ARCHIVE],
  },
  PROCESSED_WITH_CHANGES: {
    header: [ACTIONS.SAVE],
    dropdown: [ACTIONS.ARCHIVE],
  },
  DELETED: {
    header: [],
    dropdown: [],
  },
  SUBMITTED: {
    header: [],
    dropdown: [],
  },
  EXPIRED: {
    header: [],
    dropdown: [],
  },
}

const useActions = (Actions, status, hasChanges, stateToActions) => {
  const [actions, setActions] = useState([])
  const [dropdownActions, setDropdownActions] = useState([])

  let statusTransformed = status.toUpperCase()

  if (hasChanges && [STATUSES.APPROVED, STATUSES.PROCESSED].includes(status)) {
    statusTransformed = `${status}_WITH_CHANGES`.toUpperCase()
  }

  useEffect(() => {
    const { header, dropdown } = stateToActions[statusTransformed]
    setActions(header.map((action) => Actions[action]))
    setDropdownActions(dropdown.map((action) => Actions[action]))
  }, [Actions, statusTransformed])

  return { actions, dropdownActions }
}

const AudienceCreatorHeader = ({
  title,
  onSaveDraft,
  onApprove,
  onArchive,
  onDelete,
  onChangelog,
  status,
  loading,
  hasChanges,
  taxonomySlug,
  extraActions,
}) => {
  const moreInfo = useDropdown()
  const moreOptions = useDropdown()
  const history = useHistory()

  const Actions = useMemo(
    () => ({
      [ACTIONS.EXIT]: () => (
        <Button
          onClick={() => {
            if (taxonomySlug) {
              history.push(`/taxonomies/${taxonomySlug}/audiences`)
            } else {
              history.push('/audience-creator')
            }
          }}
          className="audience-creator-header__button"
          theme={BUTTON_THEME.GHOST}
        >
          Exit
        </Button>
      ),
      [ACTIONS.SAVE_AS_DRAFT]: () => (
        <Button
          onClick={onSaveDraft}
          isLoading={loading.draft}
          className="audience-creator-header__button"
          theme={BUTTON_THEME.SECONDARY}
        >
          Save as Draft
        </Button>
      ),
      [ACTIONS.APPROVE]: () => (
        <Button
          isLoading={loading.approved}
          onClick={onApprove}
          className="audience-creator-header__button"
        >
          Approve
        </Button>
      ),
      [ACTIONS.SAVE]: () => (
        <Button
          isLoading={loading.save}
          onClick={onApprove}
          className="audience-creator-header__button"
        >
          Save
        </Button>
      ),
      [ACTIONS.ARCHIVE]: () => (
        <Button
          theme={BUTTON_THEME.GHOST}
          className="audience-creator-header__dropdown-option"
          onClick={onArchive}
          isLoading={loading.archive}
        >
          <Icon type="outline" icon="openedfolder" color="#3D3D3D" width="24px" height="24px" />
          Archive
        </Button>
      ),
      [ACTIONS.DELETE]: () => (
        <Button
          theme={BUTTON_THEME.GHOST}
          className="audience-creator-header__dropdown-option"
          onClick={onDelete}
          isLoading={loading.delete}
        >
          <Icon type="outline" icon="trashcan" color="#3D3D3D" width="24px" height="24px" />
          Delete
        </Button>
      ),
    }),
    [loading, onSaveDraft, onApprove, onArchive, onDelete]
  )

  const { actions, dropdownActions } = useActions(
    Actions,
    status,
    hasChanges,
    taxonomySlug ? STATE_TO_ACTIONS_TAXONOMY : STATE_TO_ACTIONS
  )

  return (
    <Header className="audience-creator__header">
      <div className="audience-creator-header">
        <div className="title-wrapper">
          <h2 className="title">{title.split('>').slice(-1)} </h2>
          <div className="status-wrapper">
            <If condition={taxonomySlug}>
              <span className="status" title="taxonomy slug">
                {taxonomySlug}
              </span>
            </If>
            <span className="status">{status}</span>
          </div>
        </div>
        <Actions.Exit />
        {actions.map((Action, index) => (
          <Action key={index} />
        ))}
        {/* buttons the page adds, after Save - a taxonomy segment's API Integrations */}
        {extraActions}
        <If condition={dropdownActions.length > 0}>
          <IconButton
            className="audience-creator-header__more-options"
            onClick={moreOptions.handleOpen}
            isActive={moreOptions.isOpen}
            aria-label="See more options"
            iconProps={{
              icon: 'more',
              type: ICON_TYPES.FILL,
              width: '24px',
              height: '24px',
            }}
            text="More options"
          />
        </If>
        <Divider vertical className={'audience-creator-header__button-divider'} />
        <IconButton
          onClick={onChangelog}
          iconProps={{
            icon: 'news',
            type: ICON_TYPES.OUTLINE,
            width: '24px',
            height: '24px',
          }}
          text="View Changelog 🡕"
          aria-label="View Changelog"
        />
        <IconButton
          onClick={moreInfo.handleOpen}
          isActive={moreInfo.isOpen}
          aria-label="See additional help"
          iconProps={{
            icon: 'info',
            type: ICON_TYPES.OUTLINE,
            width: '24px',
            height: '24px',
          }}
          text="More info"
        />

        <If condition={moreOptions.isOpen}>
          <Card
            component="ul"
            className="audience-creator-header__dropdown"
            ref={moreOptions.dropdownRef}
          >
            {dropdownActions.map((Action, index) => (
              <Action key={index} />
            ))}
          </Card>
        </If>
        <If condition={moreInfo.isOpen}>
          <MoreInfoDropdown ref={moreInfo.dropdownRef} onClose={moreInfo.handleClose} />
        </If>
      </div>
    </Header>
  )
}

AudienceCreatorHeader.defaultProps = {
  title: '',
  onSaveDraft: () => {},
  onApprove: () => {},
  onArchive: () => {},
  onDelete: () => {},
  status: STATUSES.DRAFT,
  loading: {
    approving: false,
    savingDraft: false,
  },
  hasChanges: false,
  extraActions: null,
}

AudienceCreatorHeader.propTypes = {
  title: PropTypes.string,
  onSaveDraft: PropTypes.func,
  onApprove: PropTypes.func,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  status: PropTypes.oneOf(Object.values(STATUSES)),
  loading: PropTypes.shape({
    approving: PropTypes.bool,
    savingDraft: PropTypes.bool,
  }),
  hasChanges: PropTypes.bool,
  taxonomySlug: PropTypes.string,
  extraActions: PropTypes.node,
}

export default AudienceCreatorHeader
